import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsArrowUpRight } from "react-icons/bs";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiPaths } from "../../api/paths";
import DataTable from "../../components/data-table/DataTable";
import DropdownIcon from "../../components/icons/DropdownIcon";
import SearchIcon from "../../components/icons/SearchIcon";
import Pagination from "../../components/pagination/Pagination";
import useData from "../../hooks/use-data";
import usePagination from "../../hooks/use-pagination";
import { PaginatedApiResponseType } from "../../types/common";
import { CoverStatusType, CoverType } from "../../types/cover";
import { ActionType, ColumnType } from "../../types/data-table";
import MyCoversStats from "./MyCoversStats/MyCoversStats";
import "./mycovers.css";
import CustomButton from "../../components/custom-button/CustomButton";
import DynamicTabs from "../../components/tab/DynamicTabs";
import NautilusAsset from "../purchase-cover/nautilus-asset-page/NautilusAsset";
import useDisclosure from "../../hooks/use-disclosure";
import { useAppContext } from "../../context/app-context";
import moment from "moment";

export default function MyCovers() {
  const { t } = useTranslation(["myCovers", "common"]);
  const { keycloakProfile } = useAppContext();
  const navigate = useNavigate();
  const pagination = usePagination();
  const [searchInput, setSearchInput] = useState("");
  const [activeTab, setActiveTab] = useState<number>(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const { search, cover_status } = Object.fromEntries(searchParams);
  const { onHide, onShow, show } = useDisclosure();

  const params = useMemo(
    () => ({
      params: {
        page: pagination.page,
        limit: pagination.limit,
        email: keycloakProfile?.email,
        search,
        cover_status,
      },
    }),
    [
      pagination.page,
      pagination.limit,
      keycloakProfile?.email,
      search,
      cover_status,
    ]
  );
  const { data, loading } = useData<PaginatedApiResponseType<CoverType[]>>(
    ApiPaths.covers,
    {
      options: params,
      onError: () => {
        toast(t("details.error.fetch_table", { ns: "myCovers" }), {
          type: "error",
        });
      },
      fetchIf: !!keycloakProfile?.email,
    }
  );

  const { data: coverStatusList } = useData<CoverStatusType[]>(
    ApiPaths.coverStatus
  );

  const handleFilter = (key: string, value: string) => {
    pagination.handlePage(1);
    setSearchParams({ ...Object.fromEntries(searchParams), [key]: value });
  };

  const handleDownload = (
    base64String: string,
    fileName: string = "file.pdf"
  ) => {
    try {
      // Decode base64 string to raw binary data
      const byteCharacters = atob(base64String.split(",")[1]);
      const byteNumbers: number[] = Array.from({
        length: byteCharacters.length,
      });
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.codePointAt(i) ?? 0;
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Create an object URL for the Blob
      const url = URL.createObjectURL(blob);

      // Create a link and trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.append(link);
      link.click();
      link.remove();

      // Clean up the object URL
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download the file. Please try again.");
      console.error("Download error:", error);
    }
  };

  const columnsTraditionalCovers: ColumnType[] = [
    {
      accessor: "product.product_name",
      header: t("table_head.product_name", { ns: "myCovers" }),
      formatDisplay: (row: CoverType) => {
        return row?.product?.product_name ? (
          <div>{row?.product?.product_name}</div>
        ) : (
          "-"
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product.product_owner_name",
      header: t("table_head.product_owner_name", { ns: "myCovers" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product.product_type",
      header: t("table_head.product_type", { ns: "myCovers" }),
      formatDisplay: (row) => {
        return (
          <div>
            {row?.product?.product_type
              ?.map((item: any) => item?.label)
              .join(", ")}
          </div>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover_start_date",
      header: t("table_head.start_date", { ns: "myCovers" }),
      formatDisplay: (row: CoverType) => {
        return <div>{moment(row?.cover_start_date).format("DD/MM/YYYY")}</div>;
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover_end_date",
      header: t("table_head.end_date", { ns: "myCovers" }),
      formatDisplay: (row: CoverType) => {
        return <div>{moment(row?.cover_end_date).format("DD/MM/YYYY")}</div>;
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product.cover_wording",
      header: t("table_head.product_details", {
        ns: "myCovers",
      }),
      formatDisplay: (row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleDownload(row?.product?.cover_wording);
          }}
          rel="noreferrer"
        >
          Download <BsArrowUpRight />
        </a>
      ),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover_status",
      header: t("table_head.status", { ns: "myCovers" }),
      formatDisplay: (row) => {
        return row.cover_status ? (
          <button
            className={`status-button px-4 py-2 ${row?.cover_status?.toLowerCase()}-status`}
          >
            {row?.cover_status}
          </button>
        ) : (
          "-"
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
  ];

  const actionsTraditionalCovers: ActionType[] = [
    {
      name: "raise_new_claim",
      action: (row: CoverType) => {
        return (
          <CustomButton
            className="primary-button"
            onClick={() => {
              navigate(`${row?._id}/traditional-cover`);
            }}
          >
            {t("button.view_details", { ns: "myCovers" })}
          </CustomButton>
        );
      },
    },
  ];

  const TraditionalCovers = () => {
    return (
      <div className="mt-4 primary-table-cont">
        <DataTable
          columns={columnsTraditionalCovers}
          data={data?.data}
          showSerial={true}
          actions={actionsTraditionalCovers}
          headerBackground={"bg-dark text-white"}
          loading={loading}
        />
        <div className="mb-5">
          <Pagination total={data?.count ?? 10} pagination={pagination} />
        </div>
      </div>
    );
  };

  const tabs = [
    {
      id: 1,
      title: t("cart.traditional_insurance", { ns: "components" }),
      component: <TraditionalCovers />,
    },
    {
      id: 2,
      title: t("cart.nautilus_asset_covers", { ns: "components" }),
      component: (
        <NautilusAsset
          onHide={onHide}
          show={show}
          redirectURL="https://app.nautilusfinance.io/"
        />
      ),
    },
  ];

  const handleTabChange = (selectedTab: number) => {
    setActiveTab(selectedTab);
    if (+selectedTab === 2) {
      onShow();
    }
  };

  return (
    <div className="p-3 pb-5 p-md-4">
      <div className="rounded-2 bg-white p-4 pb-0 px-md-5 pb-md-3 mb-3">
        <div className="d-md-flex align-items-center justify-content-between mb-3">
          <h1 className="primary-title mb-3 mb-md-0">
            {t("heading", { ns: "myCovers" })}
          </h1>
          <div className="primary-input-group">
            <input
              type="search"
              className="primary-search-input"
              placeholder={
                t("placeholder.search", { ns: "myCovers" }) as string
              }
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button
              className="primary-button search-btn"
              type="button"
              onClick={() => handleFilter("search", searchInput)}
            >
              {t("button.search", { ns: "common" })}
            </button>
            <div className="d-md-none search-icon">
              <SearchIcon />
            </div>
          </div>
        </div>
        <MyCoversStats />
      </div>
      <div className="d-flex justify-content-between justify-content-md-end align-items-center mb-3">
        <div className="me-md-4 position-relative ">
          <div className="drop-down-icon">
            <DropdownIcon />
          </div>
          <select
            name=""
            id=""
            className="covers-drop-down shadow-none border-0"
            onChange={(e) => handleFilter("cover_status", e.target.value)}
            value={cover_status}
          >
            <option value="">
              {t("placeholder.all_covers", { ns: "myCovers" })}
            </option>
            {coverStatusList &&
              Array.isArray(coverStatusList) &&
              coverStatusList.map((item) => {
                return (
                  <option value={item.code} key={item._id}>
                    {item.label}
                  </option>
                );
              })}
            <option value="Expired">
              {t("filters.expired", { ns: "myCovers" })}
            </option>
            <option value="Pending">
              {t("filters.pending", { ns: "myCovers" })}
            </option>
          </select>
        </div>

        <Link
          to="/purchase-traditional-insurance"
          className="primary-button buy-covers-button text-decoration-none"
        >
          {t("button.buy_covers", { ns: "myCovers" })}
        </Link>
      </div>
      <DynamicTabs
        tabs={tabs}
        currentTab={activeTab}
        currentSelectedTab={handleTabChange}
      />
    </div>
  );
}
